import React, { useEffect, useRef } from 'react';

import Image from 'ui-components/Image';
import { ATC } from 'components/ATC';
import { Addon } from 'components/Addons/interface';
import { debounce } from 'utils/debounce';
import { HookFormProps, PDPProps } from '../interface';

interface StickyATCProps extends Pick<PDPProps, 'product'>, HookFormProps {
  addons: Addon[];
  shouldShowDualButtons: boolean;
}

const StickyATC = ({
  product,
  addons,
  shouldShowDualButtons,
  ...formProps
}: StickyATCProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => {
      // Adjust gladly position
      const gladlyContainer = document.getElementById('gladlyChat_container');
      if (gladlyContainer && containerRef.current) {
        const containerHeight = containerRef.current.offsetHeight;
        const offset = containerHeight;
        gladlyContainer.style.setProperty('bottom', `${offset}px`, 'important');
      }

      // Adjust bottom space for sticky ATC
      const root = document.getElementById('react-root');
      if (root && containerRef.current) {
        const containerHeight = containerRef.current.offsetHeight;
        root.style.paddingBottom = `${containerHeight}px`;
      }
    };

    const debouncedHandleResize = debounce(handleResize, 100);

    handleResize();
    window.addEventListener('resize', debouncedHandleResize);

    // Watch for Gladly container to be added to the DOM
    const observer = new MutationObserver(() => {
      const gladlyContainer = document.getElementById('gladlyChat_container');
      if (gladlyContainer) {
        debouncedHandleResize();
        observer.disconnect(); // Stop observing once found
      }
    });

    // Observe the entire document body for added nodes
    observer.observe(document.body, {
      childList: true,
      subtree: false,
    });

    return () => {
      window.removeEventListener('resize', debouncedHandleResize);
      observer.disconnect();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      aria-hidden
      className="fixed bottom-0 left-0 right-0 z-[1200] bg-white shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] max-sm:hidden"
    >
      <div className="flex items-end gap-3 px-3 py-3 pt-2">
        {product.main_image && (
          <div className="mt-1 w-[114px] shrink-0 overflow-hidden rounded sm:w-[140px] md:w-20 sm:max-md:landscape:w-[68px]">
            <Image
              useWebP
              isSquare
              width="100%"
              height="100%"
              src={product.main_image.cloudinary_photo_id}
              alt={`image of ${product.main_image.filename}`}
            />
          </div>
        )}
        <div className="flex min-w-0 grow flex-col justify-between gap-2 sm:gap-3 md:flex-row md:items-end sm:max-md:landscape:flex-row sm:max-md:landscape:items-end">
          <div
            className={`flex min-w-0 gap-2 overflow-x-auto pt-1 ${shouldShowDualButtons ? 'max-sm:!hidden' : ''} sm:max-md:h-[72px] md:h-[54px] md:pr-1 sm:max-md:landscape:h-[52px] sm:max-md:landscape:pr-1`}
          >
            {addons.map((addon) => (
              <div key={addon.product_id} className="relative">
                <div className="relative w-[68px] shrink-0 overflow-hidden rounded sm:max-md:w-[68px] md:w-[50px] sm:max-md:landscape:w-12">
                  <Image
                    useWebP
                    isSquare
                    width="100%"
                    height="100%"
                    src={addon.cloudinary_id}
                    alt={`image of ${addon.name}`}
                  />
                </div>
                <div className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-slate-700 bg-opacity-90 px-1.5 leading-none">
                  <span className="text-sm font-bold text-white">
                    {addon.quantity}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <div className="shrink-0 max-sm:w-full">
            <ATC
              id="sticky-atc"
              product={product}
              shouldShowDualButtons={shouldShowDualButtons}
              {...formProps}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default StickyATC;
